// Backend web FALLBACK: wa-sqlite con IDBBatchAtomicVFS (IndexedDB batch-atomico).
// Implementa el contrato WebSqliteBackend.
//
// Recomendado por PowerSync para Capacitor: esquiva la clase de bug de OPFS-handle-close
// en iOS background (OPFSCoopSync). Escribe al vuelo (batch-atomic) -> persist() no-op.
//
// API confirmada contra el paquete instalado (wa-sqlite@1.1.1, GitHub tag):
//   - Modulo Emscripten async: default export de 'wa-sqlite/dist/wa-sqlite-async.mjs'
//     (factory que recibe { locateFile } y resuelve una promesa).
//   - Factory(Module) -> sqlite3 API.
//   - IDBBatchAtomicVFS.create(name, module, options) -> vfs (static async create).
//   - sqlite3.vfs_register(vfs, makeDefault).
//   - sqlite3.open_v2(filename, flags, vfsName) -> db.
//   - sqlite3.exec(db, sql, (row, columns) => {}) — row es array, columns array de nombres.
//   - sqlite3.statements/bind_collection/step/row/column_names/reset/finalize para params.
//
// Result-shape: query() colecta filas como objetos {col: val} -> { values: rows }.

import SQLiteESMFactory from 'wa-sqlite/dist/wa-sqlite-async.mjs'
import { Factory } from 'wa-sqlite/src/sqlite-api.js'
import { IDBBatchAtomicVFS } from 'wa-sqlite/src/examples/IDBBatchAtomicVFS.js'
import * as SQLite from 'wa-sqlite/src/sqlite-constants.js'

const VFS_NAME = 'binnacle-idb-batch'
const DB_FILE = 'binnacle_offline_v2'

// Cota dura para el open() completo. Este backend es el fallback compartido por TODAS las
// pestañas 2+ (solo la primera pestaña usa opfs-sahpool, ver selectBackend.js): todas abren la
// MISMA IndexedDB (VFS_NAME/DB_FILE), asi que con varias pestañas ya contendiendo por sus
// transacciones batch-atomicas, IDBBatchAtomicVFS.create()/open_v2() puede quedar colgado
// indefinidamente sin resolver ni rechazar. Sin timeout propio, ese open() cuelga de fondo para
// siempre (aun despues de que el timeout del boot en boot/offline.js se rinda), acumulando trabajo
// fantasma y sin reportar un error propio. Mismo patron que POST_TIMEOUT_MS de OpfsSahPoolBackend.
const OPEN_TIMEOUT_MS = 60000

// LOCK_POLICY (bug real, no teorico: reproducido con 3 pestañas -- ver PR de la coordinacion entre
// pestañas): IDBBatchAtomicVFS extiende WebLocksMixin, que implementa el protocolo de locks de
// SQLite (SHARED/RESERVED/EXCLUSIVE) sobre navigator.locks. Sin opciones explicitas usa el DEFAULT
// de la libreria -- lockPolicy: 'exclusive', lockTimeout: Infinity -- que pide un Web Lock modo
// EXCLUSIVE incluso para un simple SQLITE_LOCK_SHARED (necesario solo para LEER/abrir): con eso,
// SOLO UNA pestaña puede tener CUALQUIER lock a la vez, sin limite de tiempo propio. Contradice el
// comentario de arriba ("compartido y multi-tab-safe por diseño"): la 3a pestaña (o cualquiera
// mientras otra tenga la conexion activa) se queda esperando ese lock hasta que OPEN_TIMEOUT_MS/
// BOOT_STEP_TIMEOUT_MS la rinden -- 16-20s de espera real, no un cuelgue infinito, pero tampoco
// multi-tab de verdad. 'shared' es el modo que la propia libreria ofrece para esto: SQLITE_LOCK_SHARED
// se pide en modo Web Lock 'shared' (multiples pestañas pueden tenerlo a la vez, lectores concurrentes
// de verdad); solo RESERVED/EXCLUSIVE (escrituras) siguen serializados entre pestañas, como debe ser
// en SQLite.
//
// lockTimeout CORTO a proposito (4s, no 15s): reproducido en vivo que con 'shared' el open()/health-
// check ya resuelven rapido (~150ms), pero el PRIMER paso de boot que escribe (recoverInFlight, un
// UPDATE idempotente in_flight->pending) puede toparse con OTRA pestaña que sigue con un lock de
// escritura tomado (su propio sync worker) -- y como los pasos del boot corren uno tras otro (await
// secuencial), esa espera bloquea igual el arranque de la app aunque el paso sea "tolerante" (no
// aborta la secuencia si falla). "Tolerante a fallar" no es lo mismo que "no bloquea el tiempo": sin
// este limite corto, un lockTimeout generoso (15s, el valor original) SI evita el cuelgue infinito,
// pero deja al usuario esperando esos 15s completos para algo que de todas formas esta diseñado para
// autorepararse en el proximo arranque (recoverInFlight, migrateV1Answers) -- mejor fallar rapido y
// dejar que el self-heal existente lo reintente, que hacer esperar al usuario por un lock ajeno.
const LOCK_POLICY_OPTIONS = { lockPolicy: 'shared', lockTimeout: 4000 }

export class WaSqliteIdbBackend {
  constructor () {
    this.name = 'wa-sqlite-idb'
    this._sqlite3 = null
    this._db = null
    this._vfs = null
  }

  async open () {
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        reject(new Error(`[offline-v2][wa-sqlite-idb] open() timeout (${OPEN_TIMEOUT_MS}ms)`))
      }, OPEN_TIMEOUT_MS)
      this._doOpen().then(
        (value) => { clearTimeout(timer); resolve(value) },
        (error) => { clearTimeout(timer); reject(error) }
      )
    })
  }

  async _doOpen () {
    // El .wasm se sirve como asset estatico (copy-wasm.cjs -> public/assets).
    const module = await SQLiteESMFactory({
      locateFile: (file) => '/assets/' + file
    })
    const sqlite3 = Factory(module)
    this._sqlite3 = sqlite3

    const vfs = await IDBBatchAtomicVFS.create(VFS_NAME, module, LOCK_POLICY_OPTIONS)
    this._vfs = vfs
    sqlite3.vfs_register(vfs, /* makeDefault */ false)

    this._db = await sqlite3.open_v2(
      DB_FILE,
      SQLite.SQLITE_OPEN_READWRITE | SQLite.SQLITE_OPEN_CREATE,
      VFS_NAME
    )

    // Pragmas best-effort FUERA de transaccion (synchronous revienta dentro de txn).
    try {
      await sqlite3.exec(this._db, 'PRAGMA synchronous=NORMAL;')
    } catch (e) {
      console.warn('[offline-v2][wa-sqlite] pragma synchronous fallo:', String(e))
    }
  }

  /** Normaliza a { values: [objetos] }. Usa statements+bind si hay params. */
  async query (sql, params) {
    const rows = []
    const sqlite3 = this._sqlite3
    if (params && params.length) {
      for await (const stmt of sqlite3.statements(this._db, sql)) {
        sqlite3.bind_collection(stmt, params)
        const columns = sqlite3.column_names(stmt)
        while (await sqlite3.step(stmt) === SQLite.SQLITE_ROW) {
          rows.push(rowToObject(sqlite3.row(stmt), columns))
        }
      }
    } else {
      await sqlite3.exec(this._db, sql, (row, columns) => {
        rows.push(rowToObject(row, columns))
      })
    }
    return { values: rows }
  }

  async run (sql, params) {
    const sqlite3 = this._sqlite3
    if (params && params.length) {
      for await (const stmt of sqlite3.statements(this._db, sql)) {
        sqlite3.bind_collection(stmt, params)
        while (await sqlite3.step(stmt) === SQLite.SQLITE_ROW) { /* drain */ }
      }
    } else {
      await sqlite3.exec(this._db, sql)
    }
    // changes() = filas afectadas por el último statement. Sin esto run() devolvía {} -> el coalesce
    // de BaseRepository (_saveOptimisticTx UPDATE-then-INSERT) leía affected=0 SIEMPRE e insertaba un
    // 2º op pending en vez de coalescer (inflaba el outbox). Uniforma el contrato con los otros backends.
    return { changes: { changes: this._sqlite3.changes(this._db) } }
  }

  async execute (statements) {
    await this._sqlite3.exec(this._db, statements)
    return {}
  }

  /** Batch atomico cero-perdida: BEGIN ... COMMIT manual sobre prepared statements. */
  async executeSet (set) {
    const sqlite3 = this._sqlite3
    await sqlite3.exec(this._db, 'BEGIN')
    try {
      for (const s of set) {
        for await (const stmt of sqlite3.statements(this._db, s.statement)) {
          if (s.values && s.values.length) sqlite3.bind_collection(stmt, s.values)
          while (await sqlite3.step(stmt) === SQLite.SQLITE_ROW) { /* drain */ }
        }
      }
      await sqlite3.exec(this._db, 'COMMIT')
    } catch (error) {
      try { await sqlite3.exec(this._db, 'ROLLBACK') } catch { /* noop */ }
      throw error
    }
    return {}
  }

  async transaction (fn) {
    const sqlite3 = this._sqlite3
    await sqlite3.exec(this._db, 'BEGIN')
    try {
      const result = await fn(this)
      await sqlite3.exec(this._db, 'COMMIT')
      return result
    } catch (error) {
      try { await sqlite3.exec(this._db, 'ROLLBACK') } catch { /* noop */ }
      throw error
    }
  }

  /** IDB batch-atomic escribe al vuelo: no-op. */
  async persist () {}

  /**
   * Seam reopen-on-resume. IDBBatchAtomicVFS no sufre la clase de bug OPFS-handle-close,
   * pero si el handle de la DB quedo cerrado tras background lo re-abre (modulo+VFS ya
   * registrados). Si el modulo entero se perdio, re-corre open().
   */
  async reopen () {
    if (!this._sqlite3 || !this._vfs) {
      await this.open()
      return
    }
    if (this._db != null) return // conexion viva: nada que hacer.
    try {
      this._db = await this._sqlite3.open_v2(
        DB_FILE,
        SQLite.SQLITE_OPEN_READWRITE | SQLite.SQLITE_OPEN_CREATE,
        VFS_NAME
      )
    } catch (e) {
      console.warn('[offline-v2][wa-sqlite] reopen fallo, re-abriendo completo:', String(e && e.message ? e.message : e))
      await this.open()
    }
  }

  async close () {
    if (this._db != null) {
      await this._sqlite3.close(this._db)
      this._db = null
    }
  }
}

function rowToObject (row, columns) {
  const obj = {}
  for (let i = 0; i < columns.length; i++) obj[columns[i]] = row[i]
  return obj
}
